import React, { useEffect, useState, useCallback } from "react";
import Menu from "./Menu/Menu";
import useSize from "../../../Utils/useSize/useSize";
import Backend from "../../../Backend/Backend";
import toast from "react-hot-toast";
import Loading from "../../../Utils/Loading/Loading";
import useSound from "use-sound";
import ErrorSoundFile from "../../../Sound/Error.wav";
import NotFound from "../../../Utils/NotFound/NotFound";
import TotalCard from "./TotalCard/TotalCard";
import TradeCard from "./TradeCard";
import moment from "moment";
import Filter from "./Filter/Filter";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
const backend = await new Backend().init();

export default function Trade() {
  const userType = Number(localStorage.getItem("user_type") || 7);
  const [playError] = useSound(ErrorSoundFile);
  const windowSize = useSize();

  // --- Data States ---
  const [trade, setTrade] = useState([]);
  const [tradeSearch, setTradeSearch] = useState([]);
  const [commission, setCommission] = useState(0);
  const [booked, setBooked] = useState(0);
  const [nseCash, setNseCash] = useState(0);
  const [rent, setRent] = useState(0);
  const [tradeDelete, setTradeDelete] = useState(false);
  const [commissionShow, setCommissionShow] = useState(false);

  const [isDollar, setisDollar] = useState(0);

  // --- UI States ---
  const [loading, setLoading] = useState(false);
  const [isFetchedOnce, setIsFetchedOnce] = useState(false);

  // --- Filters ---
  const [userId, setUserId] = useState(0);
  const [masterId, setMasterId] = useState(0);
  const [superId, setSuperId] = useState(0);
  const [start, setStart] = useState(
    moment().startOf("week").format("YYYY-MM-DD"),
  );
  const [end, setEnd] = useState(moment().endOf("week").format("YYYY-MM-DD"));

  // ✅ Centralized API Response Handler
  const handleTradeResponse = useCallback(
    (res) => {
      if (!res) return;

      if (res.error === "False") {
        setTrade(res.order || []);
        setTradeSearch(res.order || []);
        setCommission(res.commission || 0);
        setBooked(res.booked || 0);
        setNseCash(res.nse_cash || 0);
        setRent(res.rent || 0);
        setTradeDelete(res.trade_delete || false);
        setCommissionShow(res.commission_show || false);
        setisDollar(res.isDollar);
      } else {
        if (res.logout === 1 && window.LogOut) {
          window.LogOut();
        } else {
          playError();
          toast.error(res.message || "Something went wrong");
        }
      }
    },
    [playError],
  );

  // ✅ Build payload dynamically based on user type
  const buildPayload = useCallback(
    (extraParams = {}) => {
      const base = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        start,
        end,
        ...extraParams,
      };

      switch (userType) {
        case 1:
          return {
            ...base,
            super_id: superId,
            master_id: masterId,
            user_id: userId,
          };
        case 2:
          return {
            ...base,
            super_id: localStorage.getItem("id"),
            master_id: masterId,
            user_id: userId,
          };
        case 3:
          return {
            ...base,
            master_id: localStorage.getItem("id"),
            user_id: userId,
          };
        case 4:
          return { ...base, user_id: localStorage.getItem("id") };
        case 5:
          return {
            ...base,
            broker_id: localStorage.getItem("id"),
            user_id: userId,
          };
        default:
          return base;
      }
    },
    [userType, start, end, userId, masterId, superId],
  );

  // ✅ Unified Fetch Function
  const fetchTrade = useCallback(
    async (extraParams = {}) => {
      setLoading(true);
      try {
        const payload = buildPayload(extraParams);

        let res;
        switch (userType) {
          case 1:
            res = await backend.trade_admin(payload);
            break;
          case 2:
            res = await backend.trade_super(payload);
            break;
          case 3:
            res = await backend.trade_master(payload);
            break;
          case 4:
            res = await backend.trade(payload);
            break;
          case 5:
            res = await backend.trade_broker(payload);
            break;
          default:
            toast.error("Invalid user type");
            return;
        }

        handleTradeResponse(res);
      } catch (err) {
        playError();
        toast.error("Network error while loading trades");
        console.error("Trade fetch failed:", err);
      } finally {
        setLoading(false);
        setIsFetchedOnce(true);
      }
    },
    [userType, buildPayload, handleTradeResponse, playError],
  );

  // ✅ Initial Fetch
  useEffect(() => {
    fetchTrade();
  }, [fetchTrade]);

  const handleFilter = useCallback(
    ({ super_id, master_id, user_id, start, end }) => {
      setSuperId(super_id || 0);
      setMasterId(master_id || 0);
      setUserId(user_id || 0);
      setStart(start);
      setEnd(end);

      fetchTrade({
        super_id,
        master_id,
        user_id,
        start,
        end,
      });
    },
    [fetchTrade],
  );

  // ✅ Clear Filters
  const clearFilters = useCallback(() => {
    setUserId(0);
    setMasterId(0);
    setSuperId(0);
    fetchTrade({ user_id: 0, master_id: 0, super_id: 0 });
  }, [fetchTrade]);

  // ✅ Search
  const search = useCallback(
    (query) => {
      const q = query.trim().toLowerCase();
      setTrade(
        q.length > 1
          ? tradeSearch.filter((t) => t.symbol?.toLowerCase().includes(q))
          : tradeSearch,
      );
    },
    [tradeSearch],
  );

  // ✅ Sort
  const handleSort = useCallback((sorted) => setTrade(sorted), []);

  const exportToExcel = useCallback(() => {
    if (trade.length === 0) {
      toast.error("No data available to export");
      return;
    }

    // Format rows neatly for spreadsheet layout
    const formattedData = trade.map((t, index) => ({
      "Sr No.": index + 1,
      "Trade ID": t.id || "N/A",
      "User ID": t.user_id || "N/A",
      Symbol: t.symbol || "N/A",
      Type: t.type || "N/A", // Buy / Sell
      Quantity: Number(t.qty || 0),
      Price: Number(t.price || 0),
      ...(commissionShow && { Commission: Number(t.commission || 0) }),
      Time: t.time ? moment(t.time).format("YYYY-MM-DD HH:mm:ss") : "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Trades");

    // Save file dynamically named with current dates
    XLSX.writeFile(workbook, `Trade_Report_${start}_to_${end}.xlsx`);
    toast.success("Excel file downloaded successfully!");
  }, [trade, commissionShow, start, end]);

  //  --- PDF Export Handler ---
  const exportToPDF = useCallback(() => {
    if (trade.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const doc = new jsPDF("l", "pt", "a4"); // Landscape layout gives tables room to breathe

    // Title and Meta
    doc.setFontSize(16);
    doc.text("Trade Report", 40, 40);
    doc.setFontSize(10);
    doc.text(`Duration: ${start} to ${end}`, 40, 55);

    // Setup columns conditionally based on privileges
    const headers = ["Sr No.", "Trade ID", "User ID", "Symbol", "Type", "Qty", "Price"];
    if (commissionShow) headers.push("Commission");
    headers.push("Time");

    // Dynamic data mapping
    const tableRows = trade.map((t, index) => {
      const row = [
        index + 1,
        t.id || "N/A",
        t.user_id || "N/A",
        t.symbol || "N/A",
        t.type || "N/A",
        t.qty || 0,
        t.price || 0,
      ];
      if (commissionShow) row.push(t.commission || 0);
      row.push(t.time ? moment(t.time).format("YYYY-MM-DD HH:mm") : "N/A");
      return row;
    });

    // AutoTable layout logic
    doc.autoTable({
      head: [headers],
      body: tableRows,
      startY: 70,
      theme: "striped",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
    });

    doc.save(`Trade_Report_${start}_to_${end}.pdf`);
    toast.success("PDF report downloaded successfully!");
  }, [trade, commissionShow, start, end]);

  return (
    <div>
      <Filter
        clear={clearFilters}
        search={search}
        onFilter={handleFilter} // ✅ unified filter
        records={trade.length}
        userType={userType}
        onExportExcel={exportToExcel} // ✅ Passed to UI
        onExportPDF={exportToPDF}
      />

      {/* --- Totals Section --- */}
      <TotalCard
        commissionShow={commissionShow}
        commission={commission}
        booked={booked}
        rent={rent}
        nseCash={nseCash}
        userType={userType}
        trade={trade}
        isDollar={isDollar}
      />

      {/* --- Menu --- */}
      <Menu
        userType={userType}
        trade_delete={tradeDelete}
        trade_search={tradeSearch}
        handlewSort={handleSort}
        commissionShow={commissionShow}
        isDollar={isDollar}
      />

      {/* --- Trade List --- */}
      <div
        style={{
          width: windowSize[0],
          height: windowSize[1] - 265,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Loading height={windowSize[1] - 265} />
        ) : trade.length > 0 ? (
          <div
            style={{
              width: windowSize[0],
              height: windowSize[1] - 265,
              overflowY: "auto",
            }}
          >
            {trade.map((item) => (
              <TradeCard
                key={item.id}
                item={item}
                reload={fetchTrade}
                trade_delete={tradeDelete}
                userType={userType}
                commissionShow={commissionShow}
                isDollar={isDollar}
              />
            ))}
          </div>
        ) : isFetchedOnce ? (
          <NotFound
            height={windowSize[1] - 265}
            title="Nothing here"
            subtext="No Trade Found."
          />
        ) : null}
      </div>
    </div>
  );
}
